import { Dialog, DialogContent, DialogTitle, IconButton, Stack, Box, Typography, Button, InputAdornment, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatCurrency } from "src/helper/convertNum";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, FormProvider, useForm } from "react-hook-form";
import FormControlInput from "src/components/shared/FormControlInput";
import FormControlAutocomplete from "src/components/shared/FormControlAutocomplete";
import { renderBankAccountAutocompleteOption } from "src/components/shared/renderBankAccountAutocompleteOption";
import convertCurrency from "src/helper/convertCurrency";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import usePaymentAccountList from "src/hooks/debt/usePaymentAccountList";
import { useEffect, useMemo, useState } from "react";
import useDebtPayment from "src/hooks/debt/useDebtPayment";
import { enqueueSnackbar } from "notistack";
import useInfiniteAutocomplete from "src/hooks/useInfiniteAutocomplete";
import useDebounce from "src/hooks/useDebounce";
import useWarehouseDebtOut from "src/hooks/warehouse/useWarehouseDebtOut";
import { NumericFormat } from "react-number-format";
import useAuthGlobal from "src/context/useAuthGlobal";
import { userRoleType } from "src/constant";
import dayjs from "dayjs";

const getWarehouseDebtPaymentAmounts = (debtOut) => {
  const totalDebt = debtOut?.totalDebt ?? 0;
  const balance = debtOut?.balance ?? 0;
  return { totalDebt, balance, payableDebt: Math.abs(totalDebt - balance) };
};

const paymentMethodOptions = [
  { value: "Transfer", label: "Chuyển khoản" },
  { value: "Cash", label: "Tiền mặt" },
];

const RequiredLabel = ({ children, required = true }) => (
  <span>
    {children}
    {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
  </span>
);

const schema = yup.object().shape({
  transferDate: yup
    .mixed()
    .required("Ngày đi tiền trên nội dung chuyển khoản là bắt buộc")
    .test("valid-date", "Ngày không hợp lệ", (v) => v != null && dayjs(v).isValid()),
  amount: yup.number().typeError("Số tiền thanh toán là bắt buộc").min(1, "Số tiền thanh toán phải lớn hơn 0").required("Số tiền thanh toán là bắt buộc"),
  paymentMethod: yup
    .object({
      value: yup.string().required("Hình thức thanh toán là bắt buộc"),
    })
    .required("Hình thức thanh toán là bắt buộc"),
  account: yup
    .object({
      value: yup.string().required("Số tài khoản nhận thanh toán là bắt buộc"),
    })
    .required("Số tài khoản nhận thanh toán là bắt buộc"),
  warehouseDebt: yup
    .object({
      value: yup.string().required("Kho nhận là bắt buộc"),
    })
    .when("isMultiplePayment", {
      is: true,
      then: (schema) => schema.required("Kho nhận là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),
  isMultiplePayment: yup.boolean().notRequired(),
  total: yup.number().notRequired(),
});

export default function DebtBulkPaymentDialog({ open, onClose, title = "Thanh toán hàng loạt công nợ", selectedRow, isMultiplePayment, checkedList }) {
  const [searchWarehouse, setSearchWarehouse] = useState("");
  const debouncedSearchWarehouse = useDebounce(searchWarehouse, 500);
  const { userMe } = useAuthGlobal();
  const { data: bankInfoList } = usePaymentAccountList({ warehouse: isMultiplePayment ? undefined : selectedRow?.warehouse?._id });
  const warehouseAutocompleteEnabled = open && (isMultiplePayment || userMe?.role?.name === userRoleType.superAdmin);
  const {
    items: warehouseItems,
    isLoading: warehouseLoading,
    loadingMore: warehouseLoadingMore,
    hasMore: warehouseHasMore,
    onLoadMore: onLoadMoreWarehouse,
  } = useInfiniteAutocomplete(
    "/api/v1/warehouses",
    { limit: 10, searchField: "name", searchValue: debouncedSearchWarehouse || undefined },
    warehouseAutocompleteEnabled,
    debouncedSearchWarehouse,
  );
  const [warningDebtWarehouseMessage, setWarningDebtWarehouseMessage] = useState("");
  const { trigger: updateDebt } = useDebtPayment();
  const form = useForm({
    defaultValues: {
      warehouse: "",
      transferDate: dayjs(),
      amount: 0,
      paymentMethod: null,
      account: null,
      note: "",
      isMultiplePayment: false,
      total: 0,
    },
    resolver: yupResolver(schema),
  });

  const { reset, watch } = form;
  const { amount, warehouse: warehouseField, warehouseDebt: warehouseDebtField } = watch();

  const collectorWarehouseId = useMemo(() => {
    if (!isMultiplePayment) return selectedRow?.warehouse?._id;
    if (userMe?.role?.name === userRoleType.superAdmin) {
      return warehouseField?.value ?? warehouseField ?? checkedList?.[0]?.warehouse?._id;
    }
    return userMe?.currentWarehouse?._id ?? checkedList?.[0]?.warehouse?._id;
  }, [isMultiplePayment, selectedRow?.warehouse?._id, userMe?.role?.name, userMe?.currentWarehouse?._id, warehouseField, checkedList]);

  const payerWarehouseId = isMultiplePayment ? warehouseDebtField?.value : selectedRow?.warehouseDebt?._id;

  const { data: warehouseDebtOut } = useWarehouseDebtOut(isMultiplePayment && open ? collectorWarehouseId : null, isMultiplePayment && open ? payerWarehouseId : null);

  const checkedListIds = useMemo(() => (Array.isArray(checkedList) ? checkedList.map((i) => i?._id).join(",") : ""), [checkedList]);

  useEffect(() => {
    if (!open) return;
    reset({
      warehouse: selectedRow?.warehouse?._id ?? "",
      transferDate: dayjs(),
      amount: 0,
      paymentMethod: null,
      account: null,
      note: "",
      isMultiplePayment: Boolean(isMultiplePayment),
      total: isMultiplePayment
        ? getWarehouseDebtPaymentAmounts(warehouseDebtOut?.data).payableDebt
        : getWarehouseDebtPaymentAmounts({ totalDebt: selectedRow?.total ?? 0, balance: selectedRow?.subTotal ?? 0 }).payableDebt,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ reset khi mở dialog / đổi phiếu; tránh reset khi đang sửa ngày
  }, [open, selectedRow?._id, isMultiplePayment, checkedListIds, reset]);

  const handleClose = () => {
    form.reset();
    setWarningDebtWarehouseMessage("");
    onClose?.();
  };

  const handleSubmit = async (data) => {
    try {
      if (selectedRow?._id || isMultiplePayment) {
        const body = {
          method: data?.paymentMethod?.value,
          totalReceived: data?.amount,
          paymentDate: dayjs(data?.transferDate).toISOString(),
          note: data?.note,
          customer: selectedRow?.customer?._id,
          warehouseDebt: data?.warehouseDebt?.value,
          innCode: isMultiplePayment ? checkedList?.map((item) => item?._id) : [selectedRow?._id],
          bankInfo: data?.account?.value,
        };

        const res = await updateDebt({
          body,
        });

        if (res?.success) {
          handleClose();
          return enqueueSnackbar("Thanh toán thành công", { variant: "success" });
        } else {
          return enqueueSnackbar(res?.error?.message ?? "Lỗi khi thanh toán", { variant: "error" });
        }
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const handleCheckChangeWarehouseDebt = (data) => {
    const item = checkedList?.[0];
    if (item?.warehouseDebt?._id && data?.value !== item?.warehouseDebt?._id) {
      setWarningDebtWarehouseMessage("Lưu ý: chọn kho khác đề xuất, đồng nghĩa thanh toán tất cả hoá đơn từ kho bạn đã thay đổi");
    } else {
      setWarningDebtWarehouseMessage("");
    }
  };

  useEffect(() => {
    if (isMultiplePayment && checkedList?.length) {
      const item = checkedList?.[0];
      if (item?.warehouseDebt?._id)
        form.setValue("warehouseDebt", {
          value: item?.warehouseDebt?._id ?? "",
          label: item?.warehouseDebt?.name ?? "",
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedList, isMultiplePayment]);

  const accountOptions = useMemo(() => {
    return bankInfoList?.data?.map((bankInfo) => ({
      value: bankInfo?._id,
      label: [bankInfo?.bankName, bankInfo.ownerName, bankInfo?.accountNumber].filter(Boolean).join(" - "),
      logo: bankInfo?.bank?.logo ?? bankInfo?.logo ?? null,
    }));
  }, [bankInfoList]);

  const warehouseOptions = useMemo(() => {
    return warehouseItems.map((warehouse) => ({
      value: warehouse?._id,
      label: warehouse?.name,
    }));
  }, [warehouseItems]);

  const handleInputChangeWarehouse = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchWarehouse("");
      return;
    }
    setSearchWarehouse(value);
  };

  const warehouseAutocompleteProps = {
    loading: warehouseLoading,
    hasMore: warehouseHasMore,
    loadingMore: warehouseLoadingMore,
    onLoadMore: onLoadMoreWarehouse,
    filterOptions: (opts) => opts,
    onInputChange: handleInputChangeWarehouse,
  };

  const dataRender = useMemo(() => {
    const warehouse = isMultiplePayment ? userMe?.warehouse?.name : (selectedRow?.warehouse?.name ?? "-");
    const warehouseDebt = isMultiplePayment ? (warehouseDebtField?.label ?? checkedList?.[0]?.warehouseDebt?.name ?? "-") : (selectedRow?.warehouseDebt?.name ?? "-");

    if (isMultiplePayment) {
      const { totalDebt, balance, payableDebt } = getWarehouseDebtPaymentAmounts(warehouseDebtOut?.data);
      return { warehouse, warehouseDebt, totalDebt, balance, payableDebt };
    }

    const total = selectedRow?.total ?? 0;
    const subTotal = selectedRow?.subTotal ?? 0;
    return {
      warehouse,
      warehouseDebt,
      totalDebt: total,
      balance: subTotal,
      payableDebt: Math.abs(total - subTotal),
    };
  }, [selectedRow, isMultiplePayment, checkedList, userMe?.warehouse?.name, warehouseDebtField, warehouseDebtOut?.data]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: "bold", px: "32px" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {title}
          <IconButton
            aria-label="close"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 3 }}>
        <FormProvider {...form}>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
                fontWeight="bold"
              >
                Kho xuất/ NCC
              </Typography>
              {userMe?.role?.name === userRoleType.superAdmin ? (
                <FormControlAutocomplete
                  name="warehouse"
                  placeholder="Chọn kho xuất/ NCC"
                  options={warehouseOptions}
                  size="small"
                  {...warehouseAutocompleteProps}
                />
              ) : (
                <Typography fontWeight="bold">{dataRender?.warehouse}</Typography>
              )}
            </Stack>

            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
                fontWeight="bold"
              >
                <RequiredLabel required={isMultiplePayment}>Kho nhận</RequiredLabel>
              </Typography>
              {isMultiplePayment ? (
                <>
                  <FormControlAutocomplete
                    name="warehouseDebt"
                    placeholder="Chọn kho nhận"
                    options={warehouseOptions}
                    size="small"
                    onChange={handleCheckChangeWarehouseDebt}
                    {...warehouseAutocompleteProps}
                  />
                  {warningDebtWarehouseMessage && isMultiplePayment && (
                    <Typography
                      fontSize={12}
                      color="error"
                    >
                      {warningDebtWarehouseMessage}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography fontWeight="bold">{dataRender?.warehouseDebt}</Typography>
              )}
            </Stack>

            <Stack spacing={0.5}>
              <Typography
                fontSize={12}
                color="#989898"
                fontWeight="bold"
              >
                Tổng công nợ cần thanh toán:
              </Typography>
              <Typography fontWeight="bold">{formatCurrency(dataRender?.payableDebt ?? 0)}</Typography>
            </Stack>

            <Stack spacing={0.5}>
              <Typography
                fontSize={12}
                color="#989898"
                fontWeight="bold"
              >
                Số dư chưa gạt nợ:
              </Typography>
              <Typography fontWeight="bold">{formatCurrency(dataRender?.balance ?? 0)}</Typography>
            </Stack>

            <Stack spacing={1}>
              <Typography
                fontSize={12}
                fontWeight="bold"
                color="#989898"
              >
                <RequiredLabel>Ngày đi tiền trên nội dung chuyển khoản</RequiredLabel>
              </Typography>
              <Controller
                control={form.control}
                name="transferDate"
                render={({ field, formState: { errors } }) => (
                  <DatePicker
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        error: !!errors.transferDate,
                        helperText: errors.transferDate?.message,
                      },
                    }}
                    value={dayjs.isDayjs(field.value) ? field.value : field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => field.onChange(newValue ?? null)}
                  />
                )}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography
                fontSize={12}
                fontWeight="bold"
                color="#989898"
              >
                <RequiredLabel>Nhập số tiền thanh toán</RequiredLabel>
              </Typography>
              <Controller
                control={form.control}
                name="amount"
                render={({ field, formState: { errors } }) => (
                  <NumericFormat
                    fullWidth
                    InputProps={{
                      inputProps: { min: 0 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography>đ{field?.value ? "" : 0}</Typography>
                        </InputAdornment>
                      ),
                    }}
                    thousandSeparator
                    customInput={TextField}
                    size="small"
                    error={!!errors?.amount}
                    helperText={errors?.amount?.message || ""}
                    value={field.value}
                    onValueChange={(values) => field.onChange(values.floatValue ?? "")}
                    onInput={(e) => {
                      if (e.target.value.length > 10) {
                        e.target.value = e.target.value.slice(0, 10);
                      }
                    }}
                  />
                )}
              />
              {amount > 0 && (
                <Typography
                  fontSize={10}
                  color="#989898"
                  fontStyle="italic"
                >
                  {convertCurrency(amount)} đồng
                </Typography>
              )}
            </Stack>

            <Stack spacing={1}>
              <Typography
                fontSize={12}
                fontWeight="bold"
                color="#989898"
              >
                <RequiredLabel>Hình thức thanh toán</RequiredLabel>
              </Typography>
              <FormControlAutocomplete
                name="paymentMethod"
                placeholder="Chọn hình thức thanh toán"
                options={paymentMethodOptions}
                size="small"
              />
            </Stack>

            <Stack spacing={1}>
              <Typography
                fontSize={12}
                fontWeight="bold"
                color="#989898"
              >
                <RequiredLabel>Số tài khoản nhận thanh toán</RequiredLabel>
              </Typography>
              <FormControlAutocomplete
                name="account"
                placeholder="Chọn số tài khoản"
                options={accountOptions}
                size="small"
                renderOption={renderBankAccountAutocompleteOption}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography
                fontSize={12}
                fontWeight="bold"
                color="#989898"
              >
                Ghi chú
              </Typography>
              <FormControlInput
                name="note"
                placeholder="Ghi chú..."
                multiline
                minRows={3}
                size="small"
              />
            </Stack>
            <Box mt={1}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                onClick={form.handleSubmit(handleSubmit)}
              >
                Xác nhận thanh toán
              </Button>
            </Box>
          </Stack>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
